/* eslint-disable react/no-unstable-nested-components */
import type { api } from '@meterup/proto';
import type { CellProps, Column } from 'react-table';
import type { PagefileMetaFn } from 'vite-plugin-pagefiles';
import { Alert, HardwareIcon, HStack, Pane, PaneContent, space } from '@meterup/atto';
import {
  checkDefinedOrThrow,
  expectDefinedOrThrow,
  lookupMACAddressOUI,
  NeutralBadge,
  PositiveBadge,
  ResourceNotFoundError,
  useOuiDataMap,
  ValueOrUnknownBadge,
} from '@meterup/common';
import { useQuery } from '@tanstack/react-query';
import React from 'react';

import { fetchClientsGql } from '../../../api/clients_api';
import { fetchControllerJSON } from '../../../api/controllers_api';
import { fetchDevices } from '../../../api/devices_api';
import { AutoTable } from '../../../components/AutoTable/AutoTable';
import { Nav } from '../../../components/Nav';
import { paths } from '../../../constants';
import useDocumentTitle from '../../../hooks/useDocumentTitle';
import { NosFeature, useNosFeatureEnabled } from '../../../utils/nosfeature';

export const Meta: PagefileMetaFn = () => ({
  path: '/controllers/:controllerName/switches',
});

export default function ControllerSwitchesList() {
  const ouiData = useOuiDataMap();
  const { controllerName } = checkDefinedOrThrow(
    Nav.useRegionParams('root', paths.pages.ControllerSwitchesList),
  );
  const isSOSEnabled = useNosFeatureEnabled(controllerName, NosFeature.SOS);

  const network = useQuery(
    ['controller', controllerName, 'json'],
    () => fetchControllerJSON(controllerName),
    {
      suspense: true,
    },
  ).data;

  expectDefinedOrThrow(network, new ResourceNotFoundError('Controller not found'));

  const devices = useQuery(
    ['controller', controllerName, 'switches'],
    () => fetchDevices(controllerName),
    {
      suspense: true,
    },
  ).data;
  const switches = devices?.switches ?? [];

  const connectedClients =
    useQuery(['clients', controllerName], () => fetchClientsGql(network.network_uuid)).data ?? [];
  const macAddrs = new Map<string, boolean>();
  connectedClients.forEach((client) => {
    macAddrs.set(client.macAddress, true);
  });

  const switchColumns: Column<api.SwitchResponse>[] = [
    {
      Header: 'Location',
      accessor: (row) => row.location,
      Cell: (props: CellProps<api.SwitchResponse, string>) => (
        <HStack spacing={space(8)}>
          <HardwareIcon icon="switch" size={16} variant="simple" />
          {props.value}
        </HStack>
      ),
    },
    {
      Header: 'MAC address',
      accessor: (row) => row.mac,
    },
    {
      Header: 'Manufacturer',
      accessor: (row) => lookupMACAddressOUI(row.mac, ouiData),
    },
    {
      Header: 'Model',
      accessor: (row) => row.model,
    },
    {
      Header: 'Serial number',
      accessor: (row) => row.serial_number,
    },
    {
      Header: 'PoE wattage',
      accessor: (row) => (row.poe_wattage ? `${row.poe_wattage}W` : ''),
    },
    {
      Header: 'Ports',
      accessor: (row) => row.port_info,
    },
    {
      Header: 'Inventory status',
      accessor: (row) => row.inventory_status,
      Cell: ValueOrUnknownBadge,
    },
  ];

  if (!isSOSEnabled) {
    switchColumns.push({
      Header: 'Status',
      accessor: (row) => row.mac,
      Cell: (props: CellProps<api.SwitchResponse, string>) => (
        <HStack spacing={space(8)}>
          {macAddrs.get(props.value) ? (
            <PositiveBadge>Connected</PositiveBadge>
          ) : (
            <NeutralBadge>Not connected</NeutralBadge>
          )}
        </HStack>
      ),
    });
  }

  useDocumentTitle('Switches', controllerName, network);

  return (
    <PaneContent>
      {isSOSEnabled && (
        <Alert
          icon="attention"
          variant="attention"
          relation="stacked"
          heading="This network contains Meter switches"
          copy="Please view dashboard for correct information about switches for this network."
        />
      )}
      <Pane>
        <AutoTable columns={switchColumns} data={switches} />
      </Pane>
    </PaneContent>
  );
}
